import React, { useState, useEffect, memo } from 'react';
import { Sparkles, Grid, Dice5, ChevronRight } from 'lucide-react';
import Background from './components/Background';
import BingoGame from './games/Bingo';
import SnakeAndLadder from './games/SnakeAndLadder';

const GameCard = memo(({ id, title, description, icon: Icon, color, onClick }) => (
  <button
    onClick={() => onClick(id)}
    className="w-full group bg-white/5 hover:bg-white/10 backdrop-blur-xl border border-white/10 hover:border-white/20 rounded-3xl p-6 flex items-center gap-6 transition-all duration-300 transform hover:-translate-y-1 text-left shadow-lg hover:shadow-2xl"
  >
    <div className={`p-4 rounded-2xl bg-gradient-to-br ${color} shadow-lg group-hover:scale-110 transition-transform duration-300`}>
      <Icon className="w-8 h-8 text-white" />
    </div>

    <div className="flex-1">
      <h3 className="text-xl font-bold text-white group-hover:text-purple-300 transition-colors">{title}</h3>
      <p className="text-slate-400 text-sm mt-1">{description}</p>
    </div>

    <ChevronRight className="w-6 h-6 text-white/20 group-hover:text-white/50 transition-all group-hover:translate-x-1" />
  </button>
));

GameCard.displayName = 'GameCard';

const App = () => {
  const [currentGame, setCurrentGame] = useState(null);

  // Auto-fill from URL if present
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('room')) {
      setCurrentGame('bingo');
    } else if (params.get('sl_room')) {
      setCurrentGame('snake');
    }
  }, []);

  const games = [
    {
      id: 'bingo',
      title: 'Bingo Party',
      description: 'Classic 5x5 Bingo with real-time multiplayer.',
      icon: Grid,
      color: 'from-purple-500 to-indigo-600',
    },
    {
      id: 'snake',
      title: 'Snakes & Ladders',
      description: 'The ultimate race to the finish line. Multiplayer fun!',
      icon: Dice5,
      color: 'from-green-500 to-emerald-600',
    }
  ];

  if (currentGame === 'bingo') {
    return <BingoGame onExit={() => setCurrentGame(null)} />;
  }

  if (currentGame === 'snake') {
    return <SnakeAndLadder onExit={() => setCurrentGame(null)} />;
  }

  return (
    <div className="min-h-screen flex flex-col items-center justify-center p-4 md:p-8">
      <Background />

      <div className="max-w-xl w-full">
        {/* Header */}
        <div className="text-center mb-12 animate-pop">
          <div className="inline-block relative">
            <div className="absolute inset-0 bg-gradient-to-r from-purple-500 to-pink-500 blur-2xl opacity-30 animate-pulse-slow"></div>
            <h1 className="relative text-5xl md:text-7xl font-black bg-gradient-to-r from-white via-purple-200 to-white bg-clip-text text-transparent flex items-center justify-center gap-4 drop-shadow-sm">
              <Sparkles className="w-10 h-10 md:w-14 md:h-14 text-purple-300" />
              HASKA GAMES
              <Sparkles className="w-10 h-10 md:w-14 md:h-14 text-purple-300" />
            </h1>
          </div>
          <p className="text-blue-200 mt-4 font-medium tracking-widest text-lg uppercase opacity-80">Choose Your Adventure</p>
        </div>

        {/* Games List */}
        <div className="grid grid-cols-1 gap-6 w-full animate-pop" style={{ animationDelay: '0.1s' }}>
          {games.map(game => (
            <GameCard
              key={game.id}
              {...game}
              onClick={setCurrentGame}
            />
          ))}
        </div>

        {/* Footer */}
        <div className="mt-12 text-center animate-pop" style={{ animationDelay: '0.2s' }}>
          <p className="text-white/20 text-sm">
            Developed by Deepmind & Sivan
          </p>
        </div>
      </div>
    </div>
  );
};

export default App;